import type { KeyboardEvent, RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import { AppConfig } from '@/config';
import { usePreferences } from '@/features/preferences';
import { useAppearance } from '@/theme/appearanceContext';
import type { Preferences } from '@/api/preferences';
import { useBreakpoints } from './useViewport';
import styles from './shell.module.css';

export type AppTab = 'qa' | 'ctx';

export const TAB_IDS: Record<AppTab, { tab: string; panel: string }> = {
  qa: { tab: 'app-tab-qa', panel: 'app-panel-qa' },
  ctx: { tab: 'app-tab-ctx', panel: 'app-panel-ctx' },
};

/** The manual URL for the UI language (`{lang}` → ja / en). */
export const manualUrl = (language: string) =>
  AppConfig.links.manual.replace(
    '{lang}',
    language.startsWith('en') ? 'en' : 'ja',
  );

const TEXT_SIZES: {
  value: Preferences['text_size'];
  short: string;
  long: string;
  className: string;
}[] = [
  { value: 'small', short: 'ts_s', long: 'ts_sl', className: styles.textSizeS },
  {
    value: 'medium',
    short: 'ts_m',
    long: 'ts_ml',
    className: styles.textSizeM,
  },
  { value: 'large', short: 'ts_l', long: 'ts_ll', className: styles.textSizeL },
];

/** Small / Medium / Large, the same preference as in the Preferences dialog. */
export const TextSizeControl = () => {
  const { t } = useTranslation();
  const { prefs, update } = usePreferences();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = TEXT_SIZES.findIndex(o => o.value === prefs.text_size);

  const choose = (index: number) => {
    update({ text_size: TEXT_SIZES[index].value });
    refs.current[index]?.focus();
  };

  // A radio group: arrow keys move and select, Tab leaves the group.
  const onKeyDown = (event: KeyboardEvent) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!step) return;
    event.preventDefault();
    choose((current + step + TEXT_SIZES.length) % TEXT_SIZES.length);
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('v2.shell.textSize')}
      className={styles.textSize}
      onKeyDown={onKeyDown}
    >
      {TEXT_SIZES.map((option, index) => {
        const label = t('v2.common.labelValue', {
          label: t('v2.shell.textSize'),
          value: t(`v2.shell.${option.long}`),
        });
        return (
          <button
            key={option.value}
            ref={el => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={index === current}
            aria-label={label}
            title={label}
            tabIndex={index === current ? 0 : -1}
            className={`${styles.textSizeOption} ${option.className}`}
            onClick={() => choose(index)}
          >
            {t(`v2.shell.${option.short}`)}
          </button>
        );
      })}
    </div>
  );
};

/**
 * The kit's single selected-tab indicator (FX4): it slides to the selected
 * tab and snaps to its width, measured because the labels differ in length.
 */
const useTabIndicator = (
  listRef: RefObject<HTMLDivElement>,
  tabRefs: RefObject<Record<AppTab, HTMLButtonElement | null>>,
  tab: AppTab,
) => {
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const measure = () => {
      const el = tabRefs.current?.[tab];
      if (!el) return;
      const next = { left: el.offsetLeft, width: el.offsetWidth };
      setBox(prev =>
        prev && prev.left === next.left && prev.width === next.width
          ? prev
          : next,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    Object.values(tabRefs.current ?? {}).forEach(
      el => el && observer.observe(el),
    );
    return () => observer.disconnect();
  }, [listRef, tabRefs, tab]);
  return box;
};

interface AppHeaderProps {
  tab: AppTab;
  onTab: (tab: AppTab) => void;
  onOpenPreferences: () => void;
  preferencesOpen: boolean;
  preferencesRef: RefObject<HTMLButtonElement>;
  onSignOut: () => void;
}

/**
 * Logo, Q&A and Guidelines & Context tabs, text size, Help, Preferences and
 * Sign out (spec §2). Below 1000 px the second tab reads Context; below
 * 760 px only the logo mark shows and Sign out moves into Preferences.
 */
const AppHeader = ({
  tab,
  onTab,
  onOpenPreferences,
  preferencesOpen,
  preferencesRef,
  onSignOut,
}: AppHeaderProps) => {
  const { t, i18n } = useTranslation();
  const { scheme } = useAppearance();
  const { narrow, shortTabs } = useBreakpoints();
  const tabRefs = useRef<Record<AppTab, HTMLButtonElement | null>>({
    qa: null,
    ctx: null,
  });
  const tabListRef = useRef<HTMLDivElement>(null);
  const indicator = useTabIndicator(tabListRef, tabRefs, tab);

  const tabs: { id: AppTab; label: string }[] = [
    { id: 'qa', label: t('v2.shell.tabQA') },
    {
      id: 'ctx',
      label: shortTabs ? t('v2.shell.tabCtxShort') : t('v2.shell.tabCtx'),
    },
  ];

  const onTabKeyDown = (event: KeyboardEvent) => {
    const order: AppTab[] = ['qa', 'ctx'];
    const index = order.indexOf(tab);
    let next: AppTab | null = null;
    if (event.key === 'ArrowRight') next = order[(index + 1) % order.length];
    if (event.key === 'ArrowLeft')
      next = order[(index - 1 + order.length) % order.length];
    if (event.key === 'Home') next = order[0];
    if (event.key === 'End') next = order[order.length - 1];
    if (!next) return;
    event.preventDefault();
    onTab(next);
    tabRefs.current[next]?.focus();
  };

  const dark = scheme === 'dark';
  const logo = narrow
    ? dark
      ? '/brand/qaAssistMarkOndark.png'
      : '/brand/qaAssistMark.png'
    : dark
      ? '/brand/qaAssistLockupOndark.png'
      : '/brand/qaAssistLockup.png';

  return (
    <header className={styles.header}>
      <div className={styles.headerBar}>
        <img
          src={logo}
          alt={t('v2.shell.product')}
          className={`${styles.logo} ${narrow ? styles.logoMark : ''}`}
          data-testid="app-logo"
          data-variant={narrow ? 'mark' : 'lockup'}
        />
        <div
          ref={tabListRef}
          role="tablist"
          aria-label={t('v2.shell.mainNav')}
          className={styles.tabs}
          onKeyDown={onTabKeyDown}
        >
          {indicator && (
            <span
              aria-hidden
              className={styles.tabIndicator}
              style={{
                width: indicator.width,
                transform: `translateX(${indicator.left}px)`,
              }}
            />
          )}
          {tabs.map(item => (
            <button
              key={item.id}
              ref={el => {
                tabRefs.current[item.id] = el;
              }}
              id={TAB_IDS[item.id].tab}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              aria-controls={TAB_IDS[item.id].panel}
              tabIndex={tab === item.id ? 0 : -1}
              className={styles.tab}
              title={item.label}
              onClick={() => onTab(item.id)}
            >
              <span className={styles.tabLabel}>{item.label}</span>
            </button>
          ))}
        </div>
        <div className={styles.headerActions}>
          <TextSizeControl />
          <a
            className={styles.iconButton}
            href={manualUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t('v2.shell.help')}${t('v2.common.paren', {
              text: t('v2.shell.opensNewTab'),
            })}`}
            title={t('v2.shell.helpDesc')}
            data-testid="header-help"
          >
            <QuestionCircleOutlined aria-hidden />
          </a>
          <button
            ref={preferencesRef}
            type="button"
            className={styles.iconButton}
            aria-label={t('v2.shell.prefs')}
            title={t('v2.shell.prefs')}
            aria-haspopup="dialog"
            aria-expanded={preferencesOpen}
            onClick={onOpenPreferences}
            data-testid="header-preferences"
          >
            <SettingOutlined aria-hidden />
          </button>
          {!narrow && (
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t('v2.shell.signOut')}
              title={t('v2.shell.signOut')}
              onClick={onSignOut}
              data-testid="header-sign-out"
            >
              <LogoutOutlined aria-hidden />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
